import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { parseNumericValue } from './pmt2Utils';

// Define the structure for each target count entry
export interface TargetCountEntry {
  asin: string;
  productName: string | null;
  totalPositiveTargets: number | null;
  exact: number | null; // Fixed: was exactPTA
  pta: number | null; // Fixed: separate PTA field
  cta: number | null;
  broad: number | null;
  tosExact: number | null;
  rosExact: number | null;
  ppExact: number | null;
  tosPTA: number | null;
  rosPTA: number | null;
  ppPTA: number | null;
  tosCTA: number | null;
  rosCTA: number | null;
  ppCTA: number | null;
}

// Define the structure for the data returned by the service
export interface TargetCountData {
  targetCounts: TargetCountEntry[];
  lastUpdated: Date | null;
}

/**
 * Fetches target count data for a specific ASIN from Firestore.
 * This function retrieves data from the synced Firestore collection.
 *
 * @param companyName The company name.
 * @param asin The ASIN to fetch data for.
 * @returns An object containing target count data for the ASIN.
 */
export const getTargetCountData = async (
  companyName: string,
  asin: string
): Promise<TargetCountData> => {

  try {
    const targetCounts: TargetCountEntry[] = [];

    if (asin) {
      // Fetch specific ASIN data
      const docRef = doc(db, 'pmt2_target_count', companyName, 'asins', asin);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const entry: TargetCountEntry = {
          asin: data.asin || asin,
          productName: data.productName || null,
          totalPositiveTargets: data.totalPositiveTargets || null,
          exact: data.exact || null, // Fixed: was exactPTA
          pta: data.pta || null, // Fixed: separate PTA field
          cta: data.cta || null,
          broad: data.broad || null,
          tosExact: data.tosExact || null,
          rosExact: data.rosExact || null,
          ppExact: data.ppExact || null,
          tosPTA: data.tosPTA || null,
          rosPTA: data.rosPTA || null,
          ppPTA: data.ppPTA || null,
          tosCTA: data.tosCTA || null,
          rosCTA: data.rosCTA || null,
          ppCTA: data.ppCTA || null,
        };
        targetCounts.push(entry);
      }
    }

    return {
      targetCounts,
      lastUpdated: targetCounts.length > 0 ? new Date() : null,
    };

  } catch (error) {
    return { targetCounts: [], lastUpdated: null };
  }
};

/**
 * Fetches target count data for all ASINs in a company.
 * This is useful for dashboard views that need to show data for multiple ASINs.
 *
 * @param companyName The company name.
 * @returns An object containing target count data for all ASINs.
 */
export const getAllTargetCountData = async (
  companyName: string
): Promise<TargetCountData> => {

  try {
    const targetCounts: TargetCountEntry[] = [];

    // Fetch all ASINs for the company
    const asinsRef = collection(db, 'pmt2_target_count', companyName, 'asins');
    const querySnapshot = await getDocs(asinsRef);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const entry: TargetCountEntry = {
        asin: data.asin || doc.id,
        productName: data.productName || null,
        totalPositiveTargets: data.totalPositiveTargets || null,
        exact: data.exact || null, // Fixed: was exactPTA
        pta: data.pta || null, // Fixed: separate PTA field
        cta: data.cta || null,
        broad: data.broad || null,
        tosExact: data.tosExact || null,
        rosExact: data.rosExact || null,
        ppExact: data.ppExact || null,
        tosPTA: data.tosPTA || null,
        rosPTA: data.rosPTA || null,
        ppPTA: data.ppPTA || null,
        tosCTA: data.tosCTA || null,
        rosCTA: data.rosCTA || null,
        ppCTA: data.ppCTA || null,
      };
      targetCounts.push(entry);
    });

    return {
      targetCounts,
      lastUpdated: targetCounts.length > 0 ? new Date() : null,
    };

  } catch (error) {
    return { targetCounts: [], lastUpdated: null };
  }
};